import { createElement, type ComponentType, type ReactNode } from 'react';
import {
  AppstoreOutlined,
  RobotOutlined,
  ToolOutlined,
  ReadOutlined,
  ApartmentOutlined,
  ProfileOutlined,
  BlockOutlined,
  ThunderboltOutlined,
  CodeOutlined,
  AudioOutlined,
  FileSearchOutlined,
  ExperimentOutlined,
  PlayCircleOutlined,
  AreaChartOutlined,
  TeamOutlined,
  RocketOutlined,
  DollarCircleOutlined,
  HeartOutlined,
  ApiOutlined,
  SafetyCertificateOutlined,
  BookOutlined,
  TableOutlined,
  GlobalOutlined,
  AuditOutlined,
  DashboardOutlined,
  SettingOutlined,
  BulbOutlined,
  CompassOutlined,
  FolderOutlined,
  StarOutlined,
  ClusterOutlined,
  DatabaseOutlined,
  FundOutlined,
} from '@ant-design/icons';

// ─────────────────────────────────────────────────────────────────────────────
// THE destination registry: every page Agent Studio can open — its stable key,
// route, icon, data scope and the access its API requires. It is the CLIENT half
// of one contract; the server half is the destination manifest
// (vNEXT OO.Business/AIInfo/StudioModules/AgentStudioDestinations.cs). Keys,
// routes, scopes and access must agree — `modules-check.mjs` in
// vNEXT docs/backlog/phase-2027/ai-innovation/agent-studio-modules/ fails when
// they don't, and StudioModuleStore reports any drift to admins at runtime.
//
// WHICH MODULE a destination belongs to is NOT here: it is database placement
// (ai.AgentStudioModuleItem), resolved per user by GET api/AgentStudioModules/Effective
// into StudioModuleStore. That resolved model is the ONE thing the rail, the
// launcher, the breadcrumbs, ⌘K and pins read. Placement can file a destination
// under a module; it can never make a route exist or grant access to one.
//
// Adding a page: a row here + its route in routes.tsx + the manifest row (bump
// both manifest versions) + a seed row that places it.
// ─────────────────────────────────────────────────────────────────────────────

/** Kept for the rail-slot state machine NavStore drives (one rail now owns the slot). */
export type Rail = 'left' | 'right';

/** Whose data the page shows: one merchant, the whole platform, or both side by side. */
export type NavScope = 'merchant' | 'platform' | 'both';

/** What opening it needs — mirrors the API behind the page; placement never changes it. */
export type NavAccess = 'studio' | 'admin' | 'claim';

export interface Destination {
  key: string;
  label: string;
  to: string;
  icon: ReactNode;
  scope: NavScope;
  access: NavAccess;
  /** Live status dot — only for items whose status is actually measured. */
  live?: 'system-health';
  /** The page has nothing to load without a merchant, so the rail greys it until one is picked. */
  lockWithoutMerchant?: boolean;
  /** A workspace's nested tabs by their first path segment, so the breadcrumb names them ("Reviews", not "reviews"). */
  tabs?: Record<string, string>;
}

/** Bump with the server manifest's ManifestVersion whenever a destination is added, removed or re-scoped. */
// 3 (2026-11-02): + scenario-intelligence (/evaluation), − approvals (its URL redirects to /evaluation/reviews).
export const STUDIO_MANIFEST_VERSION = 3;

export const HOME_KEY = 'dashboard';
export const MANAGE_MODULES_KEY = 'studio-modules';

const icon = (c: ComponentType) => createElement(c);

const D = (key: string, label: string, to: string, i: ComponentType, scope: NavScope, extra: Partial<Destination> = {}): Destination =>
  ({ key, label, to, icon: icon(i), scope, access: 'studio', ...extra });

/** The Scenario Intelligence workspace's tabs (nested routes of /evaluation, never destinations of their own). */
const EVALUATION_TABS: Record<string, string> = { coverage: 'Coverage', scenarios: 'Scenarios', runs: 'Runs', compare: 'Compare', reviews: 'Reviews' };

export const DESTINATIONS: Destination[] = [
  // Home — global, belongs to no module.
  D(HOME_KEY, 'Home', '/', AppstoreOutlined, 'merchant'),

  D('agents', 'Agents', '/agents', RobotOutlined, 'merchant', { lockWithoutMerchant: true }),
  D('workflows', 'Workflows', '/workflows', ApartmentOutlined, 'merchant', { lockWithoutMerchant: true }),

  D('skills', 'Skills', '/skills', ToolOutlined, 'merchant', { lockWithoutMerchant: true }),
  D('knowledge', 'Knowledge', '/knowledge', ReadOutlined, 'merchant', { lockWithoutMerchant: true }),
  D('briefings', 'Briefings', '/briefings', ProfileOutlined, 'merchant', { lockWithoutMerchant: true }),
  D('briefing-elements', 'Briefing elements', '/briefing-elements', BlockOutlined, 'merchant', { lockWithoutMerchant: true }),
  D('prompts', 'Prebuilt prompts', '/prompts', ThunderboltOutlined, 'merchant', { lockWithoutMerchant: true }),
  D('system-prompts', 'System prompts', '/system-prompts', CodeOutlined, 'merchant', { lockWithoutMerchant: true }),
  D('phrase-lists', 'Phrase lists', '/phrase-lists', AudioOutlined, 'merchant', { lockWithoutMerchant: true }),

  // Scenario Intelligence (manifest 3): the workspace; its tabs (/evaluation/coverage, /scenarios, /runs, /compare,
  // /reviews) are child routes and belong to it. Approvals (a coming-soon page) was retired by the same manifest.
  D('scenario-intelligence', 'Scenario Intelligence', '/evaluation', ExperimentOutlined, 'merchant', { lockWithoutMerchant: true, tabs: EVALUATION_TABS }),
  D('scenarios', 'Scenarios', '/scenarios', ExperimentOutlined, 'merchant', { lockWithoutMerchant: true }),
  D('runs', 'Recent runs', '/runs', PlayCircleOutlined, 'merchant', { lockWithoutMerchant: true }),
  D('validations', 'Analytics & trends', '/validations', AreaChartOutlined, 'merchant', { lockWithoutMerchant: true }),
  D('instruction-lint', 'Instruction lint', '/instruction-lint', FileSearchOutlined, 'merchant', { lockWithoutMerchant: true }),

  D('operations', 'Operations', '/operations', RocketOutlined, 'platform'),
  // Activity and Insights refuse to load without a merchant — the page says so (not greyed).
  D('activity', 'Activity', '/activity', ThunderboltOutlined, 'merchant'),
  D('insights', 'Insights', '/insights', AreaChartOutlined, 'merchant'),
  D('costs', 'Costs', '/costs', DollarCircleOutlined, 'platform'),
  D('system', 'System health', '/system', HeartOutlined, 'platform', { live: 'system-health' }),

  D('models', 'Models', '/models', CodeOutlined, 'platform'),
  D('pricing', 'Model pricing', '/pricing', DollarCircleOutlined, 'platform'),
  D('plugins', 'Tools', '/plugins', ApiOutlined, 'platform'),
  // Shows the platform default AND the merchant's column; saving is admin-only on the server.
  D('action-policies', 'Action policies', '/platform/action-policies', SafetyCertificateOutlined, 'both'),
  D('rule-library', 'Rule library', '/platform/rules', BookOutlined, 'platform'),
  D('lookups', 'Lookups', '/lookups', TableOutlined, 'platform'),
  // Merchant-scoped data filed under Administration: grouping does not widen its scope.
  D('reviewers', 'Reviewers', '/reviewers', TeamOutlined, 'merchant', { lockWithoutMerchant: true }),
  // Merchant/List needs the MerchantController_List claim.
  D('merchants', 'All merchants', '/platform/merchants', GlobalOutlined, 'platform', { access: 'claim' }),
  D('audit', 'Audit log', '/platform/audit', AuditOutlined, 'platform'),
  D(MANAGE_MODULES_KEY, 'Modules', '/platform/modules', AppstoreOutlined, 'platform', { access: 'admin' }),

  // Foundry module (manifest 2). Read-only; GET api/Foundry/Deployments needs only a Studio token.
  D('foundry-deployments', 'Deployments', '/foundry/deployments', ClusterOutlined, 'platform'),
];

const BY_KEY = new Map(DESTINATIONS.map((d) => [d.key, d]));

export const destinationByKey = (key: string | null | undefined): Destination | undefined => (key ? BY_KEY.get(key) : undefined);

/**
 * The destination a URL belongs to: the longest `to` that equals the path or is a
 * whole-segment prefix of it (`/agents/42/briefings` → Agents; `/system-prompts`
 * is NOT System health). `/` only matches itself. Unknown paths → undefined.
 */
export function findDestination(pathname: string): Destination | undefined {
  let best: Destination | undefined;
  for (const d of DESTINATIONS) {
    const hit = d.to === '/' ? pathname === '/' : pathname === d.to || pathname.startsWith(d.to + '/');
    if (hit && (!best || d.to.length > best.to.length)) best = d;
  }
  return best;
}

// ── Module icons: the allowlisted IconKey tokens a module may carry ──────────
// Mirrors AgentStudioModuleRules.IconKeys on the server, which refuses any other
// token on save. A token this client does not know draws the generic glyph.

export const MODULE_ICONS: Record<string, ComponentType> = {
  robot: RobotOutlined,
  book: BookOutlined,
  experiment: ExperimentOutlined,
  dashboard: DashboardOutlined,
  setting: SettingOutlined,
  appstore: AppstoreOutlined,
  tool: ToolOutlined,
  safety: SafetyCertificateOutlined,
  team: TeamOutlined,
  audit: AuditOutlined,
  api: ApiOutlined,
  heart: HeartOutlined,
  bulb: BulbOutlined,
  compass: CompassOutlined,
  folder: FolderOutlined,
  star: StarOutlined,
  rocket: RocketOutlined,
  cluster: ClusterOutlined,
  database: DatabaseOutlined,
  fund: FundOutlined,
  read: ReadOutlined,
  global: GlobalOutlined,
};

export const moduleGlyph = (iconKey: string | null | undefined): ReactNode =>
  createElement(MODULE_ICONS[iconKey ?? ''] ?? AppstoreOutlined);
